import GridLine from 'business/GridLine';
import EGridLineDirection from 'enums/EGridLineDirection';
import {
  GridWellBoundary,
  MSG_MULTIPLE_START_WELLS,
  MSG_MULTIPLE_STOP_WELLS,
  MSG_RANDOM_CONCENTRATION,
  MSG_RANDOM_PH,
  MSG_START_CONCENTRATION_NOT_SCORED,
  MSG_START_PH_NOT_SCORED,
  MSG_STOP_CONCENTRATION_NOT_SCORED,
  MSG_STOP_PH_NOT_SCORED,
} from './constants';
import {
  ConcentrationRange,
  GridWell,
  GridWellIngredient,
  GridWellPlate,
  GridWells,
  PHRange,
} from './types';

export const getRowColumn = (
  wellNumber: number,
  numColumns: number,
): { row: number; col: number } => {
  const n = wellNumber - 1;
  return { row: Math.floor(n / numColumns), col: n % numColumns };
};

export const getWellNumber = (row: number, col: number, numColumns: number): number =>
  row * numColumns + col + 1;

export const createGridWellIngredient = (
  ingredientIngredientTypeId: number,
  ingredientStockId: number,
  concentration: number,
  pH: number | undefined,
  highPhIngredientStockId: number | undefined,
): GridWellIngredient => ({
  ingredientIngredientTypeId,
  ingredientStockId,
  concentration,
  pH,
  highPhIngredientStockId,
});

export const isPHNull = (ingredient: GridWellIngredient): boolean =>
  ingredient.pH === undefined || ingredient.pH === null;

export const isHighPHIngredientStockIdNull = (ingredient: GridWellIngredient): boolean =>
  ingredient.highPhIngredientStockId === undefined ||
  ingredient.highPhIngredientStockId === null;

export const compareGridWellIngredients = (
  a: GridWellIngredient,
  b: GridWellIngredient,
): number => {
  if (a.ingredientIngredientTypeId !== b.ingredientIngredientTypeId) {
    return a.ingredientIngredientTypeId < b.ingredientIngredientTypeId ? -1 : 1;
  }
  if (a.ingredientStockId !== b.ingredientStockId) {
    return a.ingredientStockId < b.ingredientStockId ? -1 : 1;
  }
  const aHighNull = isHighPHIngredientStockIdNull(a);
  const bHighNull = isHighPHIngredientStockIdNull(b);
  if (aHighNull && !bHighNull) {
    return -1;
  }
  if (!aHighNull && bHighNull) {
    return 1;
  }
  if (
    !aHighNull &&
    !bHighNull &&
    a.highPhIngredientStockId !== b.highPhIngredientStockId
  ) {
    return a.highPhIngredientStockId < b.highPhIngredientStockId ? -1 : 1;
  }
  return 0;
};

const getMatchingIngredients = (
  well: GridWell,
  ingredient: GridWellIngredient,
): GridWellIngredient[] =>
  well.ingredients.filter((i) => compareGridWellIngredients(ingredient, i) === 0);

const hasSameIngredients = (well: GridWell, other: GridWell): boolean => {
  if (well.ingredients.length !== other.ingredients.length) {
    return false;
  }
  const wellSorted = [...well.ingredients].sort(compareGridWellIngredients);
  const otherSorted = [...other.ingredients].sort(compareGridWellIngredients);
  return wellSorted.every(
    (ingredient, i) => compareGridWellIngredients(ingredient, otherSorted[i]) === 0,
  );
};

export const createGridWellPlate = (
  plateNumber: number,
  numRows: number,
  numColumns: number,
): GridWellPlate => ({
  plateNumber,
  numRows,
  numColumns,
  wells: Array.from({ length: numRows }, () => Array(numColumns).fill(null)),
});

export const createWellInPlate = (plate: GridWellPlate, wellNumber: number): GridWell => {
  const { row, col } = getRowColumn(wellNumber, plate.numColumns);
  const well: GridWell = {
    row,
    col,
    wellNumber: getWellNumber(row, col, plate.numColumns),
    boundary: GridWellBoundary.None,
    ingredients: [],
  };
  plate.wells[row][col] = well;
  return well;
};

export const createGridWells = (): GridWells => ({
  rows: [],
  wells: [],
  startWells: [],
  stopWells: [],
  wellErrors: [],
});

const getFirstWell = (grid: GridWells): GridWell | null =>
  grid.rows.length === 0 || grid.rows[0].length === 0 ? null : grid.rows[0][0];

export const getFirstWellNumber = (grid: GridWells): number =>
  grid.wells.length === 0 ? -1 : grid.wells[0].wellNumber;

export const getLastWellNumber = (grid: GridWells): number =>
  grid.wells.length === 0 ? -1 : grid.wells[grid.wells.length - 1].wellNumber;

export const getNumRows = (grid: GridWells): number => grid.rows.length;

export const getNumColumns = (grid: GridWells): number =>
  grid.rows.length === 0 ? 0 : grid.rows[0].length;

export const getNumWells = (grid: GridWells): number =>
  grid.rows.length === 0 ? 0 : getNumRows(grid) * getNumColumns(grid);

export const getGridIngredients = (grid: GridWells): GridWellIngredient[] => {
  const first = getFirstWell(grid);
  return first ? first.ingredients : [];
};

const canAddWell = (grid: GridWells, well: GridWell): boolean => {
  const first = getFirstWell(grid);
  if (!first) {
    return true;
  }
  if (first.row !== well.row) {
    throw new Error(`Added a well not in row ${first.row}: ${well.row}`);
  }
  return hasSameIngredients(first, well);
};

const addWell = (grid: GridWells, well: GridWell): void => {
  if (grid.rows.length === 0) {
    grid.rows.push([]);
  }
  if (grid.rows.length !== 1) {
    throw new Error(`Add wells before merging: Row count: ${grid.rows.length}`);
  }
  grid.rows[0].push(well);
};

const canMergeGrid = (grid: GridWells, other: GridWells): boolean => {
  const first = getFirstWell(grid);
  const otherFirst = getFirstWell(other);
  if (!first || !otherFirst) {
    return false;
  }
  if (grid.rows[0].length !== other.rows[0].length) {
    return false;
  }
  if (first.col !== otherFirst.col) {
    return false;
  }
  if (!hasSameIngredients(first, otherFirst)) {
    return false;
  }
  if (first.row + 1 !== otherFirst.row) {
    throw new Error(`Merging disjoint rows: ${first.row}, ${otherFirst.row}`);
  }
  return true;
};

const mergeGrid = (grid: GridWells, other: GridWells): void => {
  grid.rows.push(...other.rows);
  other.rows.length = 0;
};

const initializeGroupWells = (grid: GridWells): void => {
  for (const well of grid.wells) {
    switch (well.boundary) {
      case GridWellBoundary.NearStart:
      case GridWellBoundary.ExactStart:
        grid.startWells.push(well);
        break;
      case GridWellBoundary.NearStop:
      case GridWellBoundary.ExactStop:
        grid.stopWells.push(well);
        break;
      default:
        break;
    }
  }
  if (grid.startWells.length > 1) {
    grid.wellErrors.push(MSG_MULTIPLE_START_WELLS);
  }
  if (grid.stopWells.length > 1) {
    grid.wellErrors.push(MSG_MULTIPLE_STOP_WELLS);
  }
};

export const initializeGridWells = (grid: GridWells): void => {
  grid.wells = [];
  for (const row of grid.rows) {
    grid.wells.push(...row);
  }
  initializeGroupWells(grid);
};

type BoundedValue = { value: number; boundary: GridWellBoundary };

const getMinimumConcentration = (
  wells: GridWell[],
  ingredient: GridWellIngredient,
): BoundedValue => {
  let value = Number.MAX_VALUE;
  let boundary: GridWellBoundary = GridWellBoundary.None;
  for (const well of wells) {
    for (const match of getMatchingIngredients(well, ingredient)) {
      if (value > match.concentration) {
        value = match.concentration;
        boundary = well.boundary;
      }
    }
  }
  return { value, boundary };
};

const getMaximumConcentration = (
  wells: GridWell[],
  ingredient: GridWellIngredient,
): BoundedValue => {
  let value = Number.MIN_VALUE;
  let boundary: GridWellBoundary = GridWellBoundary.None;
  for (const well of wells) {
    for (const match of getMatchingIngredients(well, ingredient)) {
      if (value < match.concentration) {
        value = match.concentration;
        boundary = well.boundary;
      }
    }
  }
  return { value, boundary };
};

const getMinimumPH = (
  wells: GridWell[],
  ingredient: GridWellIngredient,
): BoundedValue => {
  let value = Number.MAX_VALUE;
  let boundary: GridWellBoundary = GridWellBoundary.None;
  for (const well of wells) {
    for (const match of getMatchingIngredients(well, ingredient)) {
      if (isPHNull(match)) {
        continue;
      }
      if (value > match.pH) {
        value = match.pH;
        boundary = well.boundary;
      }
    }
  }
  return { value, boundary };
};

const getMaximumPH = (
  wells: GridWell[],
  ingredient: GridWellIngredient,
): BoundedValue => {
  let value = Number.MIN_VALUE;
  let boundary: GridWellBoundary = GridWellBoundary.None;
  for (const well of wells) {
    for (const match of getMatchingIngredients(well, ingredient)) {
      if (isPHNull(match)) {
        continue;
      }
      if (value < match.pH) {
        value = match.pH;
        boundary = well.boundary;
      }
    }
  }
  return { value, boundary };
};

type DirectionResult = { direction: EGridLineDirection; uniformValue: number };

const getConcentrationDirection = (
  grid: GridWells,
  ingredient: GridWellIngredient,
): DirectionResult => {
  const concentrations: number[] = [];
  for (const well of grid.wells) {
    for (const match of getMatchingIngredients(well, ingredient)) {
      concentrations.push(match.concentration);
    }
  }
  return {
    direction: GridLine.GetDirection(concentrations),
    uniformValue: concentrations.length === 0 ? 0 : concentrations[0],
  };
};

const getPHDirection = (
  grid: GridWells,
  ingredient: GridWellIngredient,
): DirectionResult => {
  const pHs: number[] = [];
  for (const well of grid.wells) {
    for (const match of getMatchingIngredients(well, ingredient)) {
      if (isPHNull(match)) {
        continue;
      }
      pHs.push(match.pH);
    }
  }
  return {
    direction: GridLine.GetDirection(pHs),
    uniformValue: pHs.length === 0 ? 0 : pHs[0],
  };
};

const formatG3 = (value: number): string => value.toString();

const getStepValues = (
  minValue: number,
  minBoundary: GridWellBoundary,
  maxValue: number,
  maxBoundary: GridWellBoundary,
  numWells: number,
): { min: number; max: number } => {
  if (minValue === maxValue) {
    return { min: minValue, max: maxValue };
  }
  let numSteps = numWells;
  if (numSteps === 0) {
    return { min: minValue, max: maxValue };
  }

  let minStep = 0;
  if (
    minBoundary === GridWellBoundary.NearStart ||
    minBoundary === GridWellBoundary.NearStop
  ) {
    numSteps++;
    minStep++;
  }

  const maxStep = numSteps - 1;
  if (
    maxBoundary === GridWellBoundary.NearStart ||
    maxBoundary === GridWellBoundary.NearStop
  ) {
    numSteps++;
  }

  const gridLine = new GridLine(minValue, maxValue, numSteps);
  return { min: gridLine.GetStepAt(minStep), max: gridLine.GetStepAt(maxStep) };
};

export const getConcentrationRange = (
  grid: GridWells,
  ingredient: GridWellIngredient,
  errors: string[],
): ConcentrationRange => {
  const { direction, uniformValue } = getConcentrationDirection(grid, ingredient);

  if (direction === EGridLineDirection.Random) {
    errors.push(MSG_RANDOM_CONCENTRATION);
    return {
      startConcentration: getMinimumConcentration(grid.wells, ingredient).value,
      stopConcentration: getMaximumConcentration(grid.wells, ingredient).value,
    };
  }

  const haveStart = grid.startWells.length > 0;
  const haveStop = grid.stopWells.length > 0;

  if (direction === EGridLineDirection.Increasing) {
    const min = getMinimumConcentration(
      haveStart ? grid.startWells : grid.wells,
      ingredient,
    );
    const max = getMaximumConcentration(
      haveStop ? grid.stopWells : grid.wells,
      ingredient,
    );

    if (haveStart && !haveStop) {
      errors.push(MSG_STOP_CONCENTRATION_NOT_SCORED);
    }
    if (!haveStart && haveStop) {
      if (max.value !== min.value) {
        errors.push(
          `Stop concentration ${formatG3(max.value)} was not the minimum ${formatG3(
            min.value,
          )}.`,
        );
      }
      min.value = 0;
      min.boundary = GridWellBoundary.NearStart;
    }
    if (haveStart && haveStop && min.value > max.value) {
      errors.push(
        `Stop concentration ${formatG3(
          max.value,
        )} was less than the start concentration ${formatG3(min.value)}.`,
      );
    }

    const stepped = getStepValues(
      min.value,
      min.boundary,
      max.value,
      max.boundary,
      getNumWells(grid),
    );
    return { startConcentration: stepped.min, stopConcentration: stepped.max };
  }

  if (direction === EGridLineDirection.Decreasing) {
    const min = getMinimumConcentration(
      haveStop ? grid.stopWells : grid.wells,
      ingredient,
    );
    const max = getMaximumConcentration(
      haveStart ? grid.startWells : grid.wells,
      ingredient,
    );

    if (!haveStart && haveStop) {
      errors.push(MSG_START_CONCENTRATION_NOT_SCORED);
    }
    if (haveStart && !haveStop) {
      if (max.value !== min.value) {
        errors.push(
          `Start concentration ${formatG3(max.value)} was not the minimum ${formatG3(
            min.value,
          )}.`,
        );
      }
      min.value = 0;
      min.boundary = GridWellBoundary.NearStop;
    }
    if (haveStart && haveStop && min.value > max.value) {
      errors.push(
        `Start concentration ${formatG3(
          max.value,
        )} was less than the stop concentration ${formatG3(min.value)}.`,
      );
    }

    const stepped = getStepValues(
      min.value,
      min.boundary,
      max.value,
      max.boundary,
      getNumWells(grid),
    );
    return { startConcentration: stepped.max, stopConcentration: stepped.min };
  }

  return { startConcentration: uniformValue, stopConcentration: uniformValue };
};

export const getPHRange = (
  grid: GridWells,
  ingredient: GridWellIngredient,
  errors: string[],
): PHRange => {
  const { direction, uniformValue } = getPHDirection(grid, ingredient);

  if (direction === EGridLineDirection.Random) {
    errors.push(MSG_RANDOM_PH);
    return {
      startPH: getMinimumPH(grid.wells, ingredient).value,
      stopPH: getMaximumPH(grid.wells, ingredient).value,
    };
  }

  const haveStart = grid.startWells.length > 0;
  const haveStop = grid.stopWells.length > 0;

  if (
    direction === EGridLineDirection.Increasing ||
    direction === EGridLineDirection.Decreasing
  ) {
    if (!haveStart && haveStop) {
      errors.push(MSG_START_PH_NOT_SCORED);
    }
    if (haveStart && !haveStop) {
      errors.push(MSG_STOP_PH_NOT_SCORED);
    }
  }

  if (direction === EGridLineDirection.Increasing) {
    const min = getMinimumPH(haveStart ? grid.startWells : grid.wells, ingredient);
    const max = getMaximumPH(haveStop ? grid.stopWells : grid.wells, ingredient);

    if (haveStart && haveStop && min.value > max.value) {
      errors.push(
        `Stop pH ${formatG3(max.value)} was less than the start pH ${formatG3(
          min.value,
        )}.`,
      );
    }
    const stepped = getStepValues(
      min.value,
      min.boundary,
      max.value,
      max.boundary,
      getNumWells(grid),
    );
    return { startPH: stepped.min, stopPH: stepped.max };
  }

  if (direction === EGridLineDirection.Decreasing) {
    const min = getMinimumPH(haveStop ? grid.stopWells : grid.wells, ingredient);
    const max = getMaximumPH(haveStart ? grid.startWells : grid.wells, ingredient);

    if (haveStart && haveStop && min.value > max.value) {
      errors.push(
        `Start pH ${formatG3(max.value)} was less than the start pH ${formatG3(
          min.value,
        )}.`,
      );
    }
    const stepped = getStepValues(
      min.value,
      min.boundary,
      max.value,
      max.boundary,
      getNumWells(grid),
    );
    return { startPH: stepped.max, stopPH: stepped.min };
  }

  return { startPH: uniformValue, stopPH: uniformValue };
};

const splitGrid = (grid: GridWells, grids: GridWells[], maxWellCount: number): void => {
  if (maxWellCount <= 0) {
    return;
  }
  if (grid.rows.length === 0) {
    return;
  }
  if (grid.rows.length !== 1) {
    throw new Error(`Split wells before merging: Row count: ${grid.rows.length}`);
  }
  const wells = grid.rows[0];
  if (wells.length <= maxWellCount) {
    return;
  }

  let wellsPerSplit = wells.length;
  for (let numSplits = 1; numSplits <= wells.length; numSplits++) {
    wellsPerSplit = Math.floor(wells.length / numSplits);
    if (wellsPerSplit <= maxWellCount && wells.length % numSplits === 0) {
      break;
    }
  }

  while (wellsPerSplit < wells.length) {
    const newGrid = createGridWells();
    grids.push(newGrid);
    for (let j = wellsPerSplit; j < wellsPerSplit * 2; j++) {
      addWell(newGrid, wells[j]);
    }
    wells.splice(wellsPerSplit, wellsPerSplit);
  }
};

const getGridsInRows = (plate: GridWellPlate, maxColumns: number): GridWells[][] => {
  const rowGrids: GridWells[][] = [];
  for (let row = 0; row < plate.numRows; row++) {
    const grids: GridWells[] = [];
    rowGrids[row] = grids;

    let grid = createGridWells();
    grids.push(grid);
    for (let col = 0; col < plate.numColumns; col++) {
      const well = plate.wells[row][col];

      if (well === null) {
        splitGrid(grid, grids, maxColumns);
        grid = createGridWells();
        grids.push(grid);
        continue;
      }

      if (!canAddWell(grid, well)) {
        splitGrid(grid, grids, maxColumns);
        grid = createGridWells();
        grids.push(grid);
      }

      addWell(grid, well);
    }
    splitGrid(grid, grids, maxColumns);
  }
  return rowGrids;
};

const mergeRowGrids = (rowGrids: GridWells[][], maxRows: number): void => {
  for (let row = rowGrids.length - 1; row > 0; row--) {
    const currentGrids = rowGrids[row - 1];
    const nextGrids = rowGrids[row];
    for (const currentGrid of currentGrids) {
      for (const nextGrid of nextGrids) {
        if (maxRows > 0 && getNumRows(nextGrid) >= maxRows) {
          continue;
        }
        if (canMergeGrid(currentGrid, nextGrid)) {
          mergeGrid(currentGrid, nextGrid);
        }
      }
    }
  }
};

const getMergedGrids = (rowGrids: GridWells[][]): GridWells[] => {
  const grids: GridWells[] = [];
  for (const gridList of rowGrids) {
    for (const grid of gridList) {
      if (getFirstWell(grid)) {
        grids.push(grid);
      }
    }
  }
  return grids;
};

export const getGrids = (
  plate: GridWellPlate,
  maxRows: number,
  maxColumns: number,
): GridWells[] => {
  const rowGrids = getGridsInRows(plate, maxColumns);
  mergeRowGrids(rowGrids, maxRows);
  const mergedGrids = getMergedGrids(rowGrids);
  for (const grid of mergedGrids) {
    initializeGridWells(grid);
  }
  return mergedGrids;
};
